import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Pagination } from '@core/models/pagination.model';
import { LoadingService } from '@core/services/loading.service';
import { MutualFundService } from '@core/services/mutual-fund.service';
import { Store } from '@ngxs/store';
import { ButtonDirective } from '@shared/directives/button.directive';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { SlideInRightModalComponent } from '@shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import { TableActions } from 'app/auth/table-store/table.actions';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-benchmark-update',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SlideInRightModalComponent,
    InputLabelComponent,
    ButtonDirective,
  ],
  templateUrl: './benchmark-update.component.html',
})
export class BenchmarkUpdateComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private notify = inject(NotificationFacade);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private loadingService = inject(LoadingService);
  private mutualFundService = inject(MutualFundService);
  private store = inject(Store);

  isLoading$: Observable<boolean>;
  subs: Subscription[] = [];

  private benchmarkId = this.activatedRoute.snapshot.params['id'];

  prefillDate: string = '';

  form = this.fb.nonNullable.group({
    rate: [null as number | null, [Validators.required, Validators.min(0)]],
  });

  constructor() {
    this.isLoading$ = this.loadingService.getLoadingObservable('update-benchmark-data');

    const state = history.state as { date?: string; rate?: number };
    if (state?.date) {
      this.prefillDate = new Date(state.date).toLocaleDateString();
      this.form.patchValue({ rate: state.rate ?? null });
    }
  }

  save() {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.notify.error('Please provide a valid rate.');
      return;
    }

    const { rate } = this.form.getRawValue();
    const sub = this.mutualFundService.updateBenchmarkData({
      id: this.benchmarkId,
      rate: rate!.toString(),
    }).subscribe({
      next: () => {
        this.notify.success('Benchmark updated successfully.');
         this.store.dispatch(new TableActions.LoadTableData({
            tableKey: 'benchmark',
            params: new Pagination({ 
              skip: 0, 
              size: 10 
            }),
          }));
        this.router.navigateByUrl('/admin/transactions/mutual-funds/benchmark');
      },
      error: () => {
        this.notify.error('Failed to update benchmark. Please try again.');
      },
    });
    this.subs.push(sub);
  }


  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }
}
